import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, XCircle, Play, Eye, Pencil, Search as SearchIcon, CheckCircle, Trash2, X, ArrowUpDown, ArrowUp, ArrowDown, Download, Edit } from 'lucide-react';
import { targetAchievementApi } from '../../../api/crm/targets/targetAchievementApi';
import targetService from '../../../api/crm/targets/targetService';
import {
  handleTextInputChange,
  handleSearchInputChange,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import { capitalizeFirst } from '../../../utils/stringUtils';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV } from '../../../utils/exportUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';

const Schedules = ({ onCreateSchedule }) => {
  const queryClient = useQueryClient();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    frequency: 'weekly',
    day: 'monday',
    time: '09:00',
    targets: [],
    enabled: true
  });
  const [errors, setErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Validation rules matching HRMS form patterns
  const validationRules = {
    name: {
      required: true,
      minLength: 3,
      maxLength: 100,
      message: 'Schedule name must be 3-100 characters'
    },
    frequency: {
      required: true,
      message: 'Please select a frequency'
    },
    day: {
      required: false, // Only required when frequency is weekly
      message: 'Please select a day'
    },
    time: {
      required: true,
      message: 'Please select a time'
    },
    targets: {
      required: true,
      minLength: 1,
      message: 'Please select at least one target'
    }
  };

  // Validate individual field
  const validateField = (field, value) => {
    const rule = validationRules[field];
    if (!rule) return null;

    // Special handling for day field - only required if frequency is weekly
    if (field === 'day' && scheduleForm.frequency !== 'weekly') {
      return null;
    }

    // Required check
    if (rule.required) {
      if (field === 'targets') {
        if (!Array.isArray(value) || value.length === 0) {
          return rule.message;
        }
      } else if (!value || value.toString().trim() === '') {
        return rule.message;
      }
    }

    // Skip other validations if field is empty and not required
    if (!value || (typeof value === 'string' && value.trim() === '')) return null;

    // Length validations
    if (rule.minLength && typeof value === 'string' && value.length < rule.minLength) {
      return `${field.charAt(0).toUpperCase() + field.slice(1)} must be at least ${rule.minLength} characters`;
    }
    if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
      return `${field.charAt(0).toUpperCase() + field.slice(1)} must not exceed ${rule.maxLength} characters`;
    }

    return null;
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};

    // Validate all fields
    Object.keys(validationRules).forEach(field => {
      const error = validateField(field, scheduleForm[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear validation error when field changes
  const clearValidationError = (field) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  const [deletingScheduleId, setDeletingScheduleId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [editingScheduleId, setEditingScheduleId] = useState(null); // null = create, id = edit
  const [showRunConfirm, setShowRunConfirm] = useState(false);
  const [runScheduleId, setRunScheduleId] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [viewingSchedule, setViewingSchedule] = useState(null); // Schedule being viewed
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [targetSearchQuery, setTargetSearchQuery] = useState('');
  const [scheduleSearchQuery, setScheduleSearchQuery] = useState('');
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });
  const [debouncedScheduleSearchQuery, setDebouncedScheduleSearchQuery] = useState('');
  const [debouncedTargetSearchQuery, setDebouncedTargetSearchQuery] = useState('');

  // Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Reset selection when search/page changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [debouncedScheduleSearchQuery, currentPage, itemsPerPage]);

  // Helper to get item date
  const getItemDate = (item) => {
    if (item.createdAt) return new Date(item.createdAt);
    // Try to extract from ID
    const id = item.id || item._id || item.scheduleId;
    if (id && /^[0-9a-fA-F]{24}$/.test(id)) {
      const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
      return new Date(timestamp);
    }
    return null;
  };

  const mapDataForExport = (data) => {
    return data.map(item => {
      const date = getItemDate(item);
      const frequencyText = item.frequency === 'weekly' && item.day
        ? `${item.frequency} (${item.day})`
        : item.frequency;

      return {
        "Schedule Name": item.name,
        "Frequency": frequencyText,
        "Time": item.time,
        "Next Run": item.nextRun ? new Date(item.nextRun).toLocaleString() : 'N/A',
        "Last Run": item.lastRun ? new Date(item.lastRun).toLocaleString() : 'N/A',
        "Status": item.enabled ? 'Active' : 'Inactive',
        "Disabled Reason": !item.enabled && item.disabledReason ? getDisabledReasonLabel(item.disabledReason) : '-',
        "Created At": date ? date.toLocaleDateString() : '-'
      };
    });
  };

  const fetchAllForExport = async (filters = {}) => {
    return await targetAchievementApi.schedules.getSchedules({
      page: 1,
      limit: 1000, // Export all limit
      search: debouncedScheduleSearchQuery.trim() || undefined,
      ...filters
    });
  };

  const handleSelectAll = (e) => {
    // Current page data
    const currentData = getSortedSchedules(schedulesData?.schedules || []);

    if (e.target.checked) {
      const allIds = currentData.map(item => item.id || item._id || item.scheduleId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      // Export selected
      const currentData = getSortedSchedules(schedulesData?.schedules || []);
      const selectedData = currentData.filter(item => {
        const id = item.id || item._id || item.scheduleId;
        return selectedRows.has(id);
      });

      const exportData = mapDataForExport(selectedData);
      exportToCSV(exportData, 'schedules_export', {
        title: 'Schedules List',
        type: 'Selected Items'
      });
      setSelectedRows(new Set());
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    const currentData = getSortedSchedules(schedulesData?.schedules || []);
    const exportData = mapDataForExport(currentData);
    exportToCSV(exportData, 'schedules_export', {
      title: 'Schedules List',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const response = await fetchAllForExport();
      const data = response.data || (Array.isArray(response) ? response : []);

      if (data.length > 0) {
        const exportData = mapDataForExport(data);
        exportToCSV(exportData, 'schedules_export', {
          title: 'Schedules List',
          type: 'All Data'
        });
        setShowExportModal(false);
      } else {
        toast.error('No data available to export');
      }
    } catch (error) {

      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const response = await fetchAllForExport();
      const sourceData = response.data || (Array.isArray(response) ? response : []);

      // Robustly parse "YYYY-MM-DD" to local start/end of day
      const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);

      const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
      const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

      const filteredData = sourceData.filter(item => {
        const itemDate = getItemDate(item);
        if (!itemDate) return false;
        // Compare timestamps directly against the full day range
        return itemDate.getTime() >= start.getTime() && itemDate.getTime() <= end.getTime();
      });

      if (filteredData.length > 0) {
        const exportData = mapDataForExport(filteredData);
        exportToCSV(exportData, 'schedules_export', {
          title: 'Schedules List',
          type: 'Date Range',
          dateRange: { startDate, endDate }
        });
        setShowExportModal(false);
      } else {
        toast.error('No data found for the selected date range');
      }
    } catch (error) {

      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  // Sorting state
  const [sortColumn, setSortColumn] = useState('nextRun');
  const [sortDirection, setSortDirection] = useState('asc');
  const searchInputRef = useRef(null);

  const getDisabledReasonLabel = (reason) => {
    switch (reason) {
      case 'target_deleted':
        return 'Disabled (linked target was deleted)';
      case 'target_expired':
        return 'Disabled (linked target period expired)';
      case 'target_completed':
        return 'Disabled (linked target was completed)';
      case 'target_inactive':
        return 'Disabled (linked target is inactive)';
      case 'manual':
        return 'Disabled manually';
      default:
        return 'Inactive';
    }
  };

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounce schedule search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedScheduleSearchQuery(scheduleSearchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [scheduleSearchQuery]);

  // Reset page when date filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [customRange.startDate, customRange.endDate]);

  // Debounce target search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTargetSearchQuery(targetSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [targetSearchQuery]);

  // React Query: Fetch schedules
  const {
    data: schedulesData,
    isLoading: loading,
    isError: schedulesError,
    error: schedulesErrorData
  } = useQuery({
    queryKey: ['schedules', debouncedScheduleSearchQuery, currentPage, itemsPerPage, customRange.startDate, customRange.endDate],
    queryFn: async () => {
      const isCustom = Boolean(customRange.startDate && customRange.endDate);

      const response = await targetAchievementApi.schedules.getSchedules({
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedScheduleSearchQuery.trim() || undefined,
        range: isCustom ? 'custom' : 'all_time',
        startDate: isCustom ? customRange.startDate : undefined,
        endDate: isCustom ? customRange.endDate : undefined
      });


      // Handle response - apiClient returns response.data directly
      if (response && response.responseCode === 200 && response.data) {
        // Standard response format: { responseCode: 200, data: [...], pagination: {...} }
        const schedulesData = Array.isArray(response.data) ? response.data : [];

        return {
          schedules: schedulesData,
          pagination: response.pagination || null
        };
      } else if (Array.isArray(response)) {
        // Direct array response (from apiClient interceptor) - fallback to client-side pagination

        return {
          schedules: response,
          pagination: null
        };
      } else {

        return {
          schedules: [],
          pagination: null
        };
      }
    },
    keepPreviousData: true
  });

  const schedules = schedulesData?.schedules || [];
  const pagination = schedulesData?.pagination || null;

  // React Query: Fetch available targets for modal
  const {
    data: targetsData,
    isLoading: searchLoading
  } = useQuery({
    queryKey: ['availableTargets', debouncedTargetSearchQuery],
    queryFn: async () => {
      const result = await targetService.getAllTargets({
        page: 1,
        limit: 100,
        search: debouncedTargetSearchQuery.trim() || undefined
      });
      if (result.success && Array.isArray(result.targets)) {
        return result.targets;
      } else if (Array.isArray(result)) {
        return result;
      }
      return [];
    },
    enabled: showScheduleModal, // Only fetch when modal is open
    keepPreviousData: true
  });

  const availableTargets = targetsData || [];

  const handleCreateSchedule = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }


    // Reset form for new schedule
    setScheduleForm({
      name: '',
      frequency: 'weekly',
      day: 'monday',
      time: '09:00',
      targets: [],
      enabled: true
    });
    setErrors({});
    setEditingScheduleId(null);
    setTargetSearchQuery(''); // Reset search when opening modal
    setShowScheduleModal(true);
  };

  // React Query: Create/Update schedule mutation
  const createUpdateScheduleMutation = useMutation({
    mutationFn: async ({ scheduleId, scheduleData }) => {
      if (scheduleId) {

        return await targetAchievementApi.schedules.updateSchedule(scheduleId, scheduleData);
      } else {

        return await targetAchievementApi.schedules.createSchedule(scheduleData);
      }
    },
    onSuccess: (response, variables) => {
      const isUpdate = !!variables.scheduleId;


      if (response && (response.responseCode === 200 || response.responseCode === 201)) {
        // Invalidate and refetch schedules
        queryClient.invalidateQueries({ queryKey: ['schedules'] });

        // Reset form
        setScheduleForm({
          name: '',
          frequency: 'weekly',
          day: 'monday',
          time: '09:00',
          reportType: 'reminder',
          targets: [],
          enabled: true
        });

        // Clear errors and editing state
        setErrors({});
        setEditingScheduleId(null);

        // Close modal
        setShowScheduleModal(false);



        // Immediate run for new schedules
        if (!isUpdate) {
          const newId = response.data?._id || response.data?.id;
          if (newId) {

            runScheduleMutation.mutate(newId);
          }
        }

        if (!isUpdate && onCreateSchedule) {
          onCreateSchedule(response.data || variables.scheduleData);
        }
      } else {
        // Extract error message from response
        let errorMsg = isUpdate ? 'Failed to update schedule' : 'Failed to create schedule';
        if (response?.responseMessage) {
          errorMsg = response.responseMessage;
        } else if (response?.error) {
          errorMsg = response.error;
        } else if (response?.message) {
          errorMsg = response.message;
        }

        // Show specific validation errors if available
        if (response?.errors && typeof response.errors === 'object') {
          const validationErrors = Object.values(response.errors).join(', ');
          if (validationErrors) {
            errorMsg += ': ' + validationErrors;
            // Set field-specific errors for better UX
            if (response.errors.recipients) {
              setErrors({ recipients: response.errors.recipients });
            }
            if (response.errors.name) {
              setErrors(prev => ({ ...prev, name: response.errors.name }));
            }
          }
        } else if (response?.data && typeof response.data === 'object') {
          const validationErrors = Object.values(response.data).join(', ');
          if (validationErrors) {
            errorMsg += ': ' + validationErrors;
          }
        }

        alert((isUpdate ? 'Failed to update schedule: ' : 'Failed to create schedule: ') + errorMsg);

      }
    },
    onError: (error) => {


      // Extract detailed error message from Axios response if available
      const errorData = error.response?.data;
      let errorMsg = 'Please try again.';

      if (errorData?.errors && typeof errorData.errors === 'object') {
        errorMsg = Object.values(errorData.errors).join('\n');
      } else if (errorData?.responseMessage) {
        errorMsg = errorData.responseMessage;
      } else if (errorData?.message) {
        errorMsg = errorData.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      alert(`Error creating schedule: ${errorMsg}`);
    }
  });

  const handleSubmitSchedule = async (e) => {
    e.preventDefault();


    // Use custom validation matching HRMS form patterns
    if (!validateForm()) {

      return;
    }

    // Create schedule data
    const scheduleData = {
      name: scheduleForm.name,
      frequency: scheduleForm.frequency,
      day: scheduleForm.frequency === 'weekly' ? scheduleForm.day : undefined,
      time: scheduleForm.time,
      reportType: 'reminder',
      targets: scheduleForm.targets,
      enabled: scheduleForm.enabled
    };

    createUpdateScheduleMutation.mutate({
      scheduleId: editingScheduleId,
      scheduleData
    });
  };

  const calculateNextRun = (frequency, day, time) => {
    const now = new Date();
    const nextRun = new Date();

    switch (frequency) {
      case 'daily':
        nextRun.setDate(now.getDate() + 1);
        break;
      case 'weekly':
        const dayMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
        const targetDay = dayMap[day];
        const daysUntilTarget = (targetDay - now.getDay() + 7) % 7;
        nextRun.setDate(now.getDate() + (daysUntilTarget === 0 ? 7 : daysUntilTarget));
        break;
      case 'monthly':
        nextRun.setMonth(now.getMonth() + 1);
        break;
      default:
        nextRun.setDate(now.getDate() + 1);
    }

    const [hours, minutes] = time.split(':');
    nextRun.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    return nextRun.toISOString();
  };

  // React Query: Toggle schedule mutation
  const toggleScheduleMutation = useMutation({
    mutationFn: async (scheduleId) => {

      return await targetAchievementApi.schedules.toggleSchedule(scheduleId);
    },
    onSuccess: (response, scheduleId) => {


      if (response && (response.responseCode === 200 || response.status === 200)) {
        // Invalidate and refetch schedules
        queryClient.invalidateQueries({ queryKey: ['schedules'] });

      } else {
        // Check if response has error structure
        const errorMsg = response?.responseMessage || response?.message || response?.error || 'Failed to toggle schedule';

        alert('Failed to toggle schedule: ' + errorMsg);
      }
    },
    onError: (error, scheduleId) => {



      // Extract error message from error response
      const errorMsg = error.response?.data?.responseMessage ||
        error.response?.data?.error ||
        error.message ||
        'Failed to toggle schedule. Please try again.';
      alert('Error toggling schedule: ' + errorMsg);
    }
  });

  const handleToggleSchedule = async (scheduleId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      // stopImmediatePropagation may not be available in all browsers/React synthetic events
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
    }

    toggleScheduleMutation.mutate(scheduleId);
  };

  // React Query: Run schedule mutation
  const runScheduleMutation = useMutation({
    mutationFn: async (scheduleId) => {

      return await targetAchievementApi.schedules.runSchedule(scheduleId);
    },
    onSuccess: (response, scheduleId) => {


      if (response && (response.responseCode === 200 || response.status === 200)) {
        // Invalidate and refetch schedules
        queryClient.invalidateQueries({ queryKey: ['schedules'] });

        // Show success message modal
        setShowSuccessModal(true);


      } else {
        const errorMsg = response?.responseMessage || response?.message || response?.error || 'Failed to run schedule';

        toast.error('Failed to run schedule: ' + errorMsg);
      }
    },
    onError: (error, scheduleId) => {



      // Extract error message from error response
      const errorMsg = error.response?.data?.responseMessage ||
        error.response?.data?.error ||
        error.message ||
        'Failed to run schedule. Please try again.';
      alert('Error running schedule: ' + errorMsg);
      toast.error('Error running schedule: ' + errorMsg);
    }
  });

  const handleRunSchedule = async (scheduleId, e) => {
    // Prevent any default behavior and event propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      // stopImmediatePropagation may not be available in all browsers/React synthetic events
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
    }

    setRunScheduleId(scheduleId);
    setShowRunConfirm(true);
  };

  const handleConfirmRun = () => {
    if (runScheduleId) {
      runScheduleMutation.mutate(runScheduleId);
      setShowRunConfirm(false);
      setRunScheduleId(null);
    }
  };

  const handleDeleteSchedule = async (scheduleId, e) => {
    // Prevent any default behavior and event propagation IMMEDIATELY
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      // stopImmediatePropagation may not be available in all browsers/React synthetic events
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
    }

    // Prevent double-click or rapid clicks
    if (loading || deletingScheduleId === scheduleId) {

      return;
    }

    // Find schedule name for better confirmation message
    const schedule = schedules.find(s => {
      const id = s.id || s._id || s.scheduleId;
      return id === scheduleId;
    });
    const scheduleName = schedule?.name || 'this schedule';

    // Show confirmation modal instead of window.confirm (to avoid Promise issues)

    setScheduleToDelete({ id: scheduleId, name: scheduleName });
    setShowDeleteConfirm(true);
  };

  // Format date & time like "15 Dec 2025, 12:53 pm"
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(',', '');
    } catch (error) {

      return 'Invalid Date';
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted schedules
  const getSortedSchedules = (schedules) => {
    if (!sortColumn || !schedules || schedules.length === 0) return schedules;

    return [...schedules].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (sortColumn === 'frequency') {
        aValue = (a.frequency || '').toLowerCase();
        bValue = (b.frequency || '').toLowerCase();
      } else if (sortColumn === 'nextRun') {
        aValue = new Date(a.nextRun || 0).getTime();
        bValue = new Date(b.nextRun || 0).getTime();
      } else if (sortColumn === 'enabled') {
        aValue = a.enabled ? 1 : 0;
        bValue = b.enabled ? 1 : 0;
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      const order = sortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Open edit modal with existing schedule data
  const handleEditSchedule = (schedule, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      const scheduleId = schedule.id || schedule._id || schedule.scheduleId;


      // Prepare targets array (ids)
      const targetIds = Array.isArray(schedule.targets)
        ? schedule.targets.map(t => t.id || t._id || t)
        : (schedule.targets || []);

      // Set form with existing values
      setScheduleForm({
        name: schedule.name || '',
        frequency: schedule.frequency || 'weekly',
        day: schedule.day || 'monday',
        time: schedule.time || '09:00',
        reportType: 'reminder',
        targets: targetIds,
        enabled: typeof schedule.enabled === 'boolean' ? schedule.enabled : true
      });

      setEditingScheduleId(scheduleId);
      setErrors({});
      setShowScheduleModal(true);
    } catch (error) {

      alert('Error loading schedule for editing. Please try again.');
    }
  };

  // React Query: Delete schedule mutation
  const deleteScheduleMutation = useMutation({
    mutationFn: async (scheduleId) => {

      return await targetAchievementApi.schedules.deleteSchedule(scheduleId);
    },
    onSuccess: (response, scheduleId) => {


      // Handle different response formats
      // Backend returns: { responseCode: 200, responseMessage: '...', data: null }
      const responseCode = response?.responseCode || response?.status;
      const responseMessage = response?.responseMessage || response?.message;

      // Check if deletion was successful
      if (responseCode === 200 || (response && responseMessage && responseMessage.includes('deleted successfully'))) {
        // Invalidate and refetch schedules
        queryClient.invalidateQueries({ queryKey: ['schedules'] });

      } else if (!response || response === null) {
        // Handle null/undefined response - might be successful DELETE

        queryClient.invalidateQueries({ queryKey: ['schedules'] });
      } else {
        const errorMsg = responseMessage || response?.error || 'Failed to delete schedule';


        alert('Failed to delete schedule: ' + errorMsg);
      }
    },
    onError: (error, scheduleId) => {



      // Check if it's a 404 (schedule not found) - might mean it was already deleted
      if (error.response?.status === 404) {

        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        return;
      }

      // Extract error message from error response
      const errorMsg = error.response?.data?.responseMessage ||
        error.response?.data?.error ||
        error.message ||
        'Failed to delete schedule. Please try again.';
      alert('Error deleting schedule: ' + errorMsg);
    }
  });

  // React Query: Bulk delete schedules mutation
  const bulkDeleteSchedulesMutation = useMutation({
    mutationFn: async (scheduleIds) => {

      return await targetAchievementApi.schedules.bulkDeleteSchedules(scheduleIds);
    },
    onSuccess: (response) => {

      if (response && (response.responseCode === 200 || response.status === 200)) {
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        setSelectedRows(new Set());
        toast.success('Schedules deleted successfully');
      } else {
        const errorMsg = response?.responseMessage || response?.message || 'Failed to delete schedules';
        toast.error(errorMsg);
      }
    },
    onError: (error) => {

      toast.error(error.message || 'Failed to delete schedules. Please try again.');
    }
  });

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = () => {
    bulkDeleteSchedulesMutation.mutate(Array.from(selectedRows));
    setShowBulkDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (!scheduleToDelete) {
      return;
    }

    const scheduleId = scheduleToDelete.id;
    const scheduleName = scheduleToDelete.name;

    // Close confirmation modal
    setShowDeleteConfirm(false);
    setScheduleToDelete(null);

    // Set deleting state
    setDeletingScheduleId(scheduleId);



    // Call mutation
    deleteScheduleMutation.mutate(scheduleId, {
      onSuccess: () => {
        alert(`Schedule "${scheduleName}" has been deleted successfully.`);
        setDeletingScheduleId(null);
      },
      onError: () => {
        setDeletingScheduleId(null);
      }
    });
  };

  const handleCancelDelete = () => {

    setShowDeleteConfirm(false);
    setScheduleToDelete(null);
    setDeletingScheduleId(null);
  };

  // Combined loading state for all mutations
  const isAnyMutationPending =
    createUpdateScheduleMutation.isPending ||
    toggleScheduleMutation.isPending ||
    runScheduleMutation.isPending ||
    deleteScheduleMutation.isPending;

  // Utility: truncate long text for cards (e.g. name, recipients preview)
  const truncateText = (value, max = 30) => {
    if (!value) return '';
    const str = String(value);
    return str.length > max ? `${str.slice(0, max)}...` : str;
  };

  const handleViewSchedule = (schedule, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setViewingSchedule(schedule);
  };

  const handleCloseView = () => {
    setViewingSchedule(null);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to page 1 when items per page changes
  };

  // Handle page change
  const handleSchedulePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination - use API pagination if available, otherwise client-side
  const totalSchedules = pagination?.total || schedules.length;
  const totalPages = pagination?.totalPages || Math.ceil(schedules.length / itemsPerPage);
  const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
  const currentPageNum = pagination?.page || currentPage;

  // If using API pagination, schedules are already paginated
  // If using client-side pagination, slice the array
  const displaySchedules = pagination
    ? schedules // API already paginated
    : schedules.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const startIndex = pagination
    ? ((currentPageNum - 1) * effectivePaginationLimit + 1)
    : ((currentPage - 1) * itemsPerPage + 1);
  const endIndex = pagination
    ? Math.min(currentPageNum * effectivePaginationLimit, totalSchedules)
    : Math.min(currentPage * itemsPerPage, totalSchedules);

  // Debug pagination


  return (
    <div className="space-y-6">
      {/* View Schedule Modal */}
      {viewingSchedule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8 max-h-[85vh] flex flex-col">
            {/* Modal header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Details</h3>
              <button
                onClick={handleCloseView}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                type="button"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Header Section */}
              <div className="border-b border-gray-200 pb-4 mb-6">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-xl font-semibold text-gray-900 break-words">
                    {viewingSchedule.name}
                  </h4>
                  <span
                    className={`px-3 py-1 text-sm font-semibold rounded-full flex-shrink-0 ${viewingSchedule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    title={
                      !viewingSchedule.enabled && viewingSchedule.disabledReason
                        ? getDisabledReasonLabel(viewingSchedule.disabledReason)
                        : viewingSchedule.enabled
                          ? 'Schedule is active'
                          : 'Schedule is inactive'
                    }
                  >
                    {viewingSchedule.enabled
                      ? 'Active'
                      : viewingSchedule.disabledReason
                        ? getDisabledReasonLabel(viewingSchedule.disabledReason)
                        : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Details Grid - Perfect Alignment */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Frequency</label>
                    <p className="text-gray-900 text-sm">
                      {capitalizeFirst(viewingSchedule.frequency)}
                      {viewingSchedule.frequency === 'weekly' && viewingSchedule.day && ` (${capitalizeFirst(viewingSchedule.day)})`}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Time</label>
                    <p className="text-gray-900 text-sm">{viewingSchedule.time}</p>
                  </div>

                  {viewingSchedule.nextRun && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Next Run</label>
                      <p className="text-gray-900 text-sm">{formatDateTime(viewingSchedule.nextRun)}</p>
                    </div>
                  )}

                  {viewingSchedule.lastRun && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Last Run</label>
                      <p className="text-gray-900 text-sm">{formatDateTime(viewingSchedule.lastRun)}</p>
                    </div>
                  )}

                  {viewingSchedule.createdAt && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Created</label>
                      <p className="text-gray-900 text-sm">
                        {formatDateTime(viewingSchedule.createdAt)}
                        {viewingSchedule.createdBy && typeof viewingSchedule.createdBy === 'string' && ` by ${viewingSchedule.createdBy}`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Targets Section - Full Width */}
                <div className="pt-2">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Targets</label>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900 text-sm first-letter:capitalize">
                      {Array.isArray(viewingSchedule.targets) && viewingSchedule.targets.length
                        ? viewingSchedule.targets
                          .map((t) => {
                            // Handle both populated objects and plain IDs
                            if (!t || typeof t !== 'object' || !t.type) {
                              return 'Unknown target';
                            }

                            const typeLabel =
                              t.type === 'revenue'
                                ? 'Revenue'
                                : t.type === 'newOpps'
                                  ? 'New Opportunities'
                                  : t.type === 'deals'
                                    ? 'Deal Count'
                                    : t.type;

                            // Fix: Properly check scope field
                            const scope = t.scope || 'unknown';
                            const scopeLabel = scope === 'team' ? 'Team' : 'Individual';

                            // For team targets, prefer teamName if available
                            let displayName = t.name || `${typeLabel} - ${t.period}`;
                            if (scope === 'team' && t.teamName && t.teamName.trim() && t.teamName !== 'Unknown') {
                              displayName = t.teamName;
                            }

                            return `${displayName} (${typeLabel}, ${scopeLabel})`;
                          })
                          .join(' • ')
                        : 'No targets selected'}
                    </p>
                  </div>
                </div>

                {/* Inactive Reason - Full Width */}
                {!viewingSchedule.enabled && viewingSchedule.disabledReason && (
                  <div className="pt-2">
                    <label className="block text-sm font-medium text-gray-600 mb-2">Inactive Reason</label>
                    <p className="text-gray-900 text-sm">
                      {getDisabledReasonLabel(viewingSchedule.disabledReason)}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={handleCloseView}
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <PermissionGate permission="target_achievement" action="update">
                  <button
                    onClick={(e) => {
                      handleCloseView();
                      handleEditSchedule(viewingSchedule, e);
                    }}
                    type="button"
                    className="btn-create px-6 py-2"
                  >
                    Edit Schedule
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && scheduleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the schedule <strong>"{scheduleToDelete.name}"</strong>?
            </p>
            <p className="text-sm text-gray-600 mb-6">
              This will permanently delete the schedule and cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                type="button"
                className="btn-cancel"
              >
                Cancel
              </button>
              <PermissionGate permission="target_achievement" action="delete">
                <button
                  onClick={handleConfirmDelete}
                  type="button"
                  disabled={deleteScheduleMutation.isPending}
                  className="px-6 py-2 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {deleteScheduleMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedRows.size} selected schedule(s)? This action cannot be undone.`}
        confirmText="Delete All"
        type="danger"
      />


      {/* Run Confirmation Modal */}
      {showRunConfirm && runScheduleId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Play className="h-6 w-6 text-brand ml-1" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Run Schedule Now</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to run this schedule immediately?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRunConfirm(false);
                  setRunScheduleId(null);
                }}
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F2942]/50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRun}
                type="button"
                disabled={runScheduleMutation.isPending}
                className="btn-create px-6 py-2"
              >
                {runScheduleMutation.isPending ? 'Running...' : 'Yes, Run Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Success</h3>
            <p className="text-sm text-gray-500 mb-6">
              Schedule run successfully.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              type="button"
              className="inline-flex justify-center w-full rounded-xl border border-transparent shadow-sm px-6 py-2.5 bg-green-600 text-base font-semibold text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm transition-all"
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <Calendar className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Schedules</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedRows.size > 0 && (
            <PermissionGate permission="target_achievement" action="delete">
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleteSchedulesMutation.isPending}
                className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete ({selectedRows.size})</span>
              </button>
            </PermissionGate>
          )}
          <button
            onClick={handleExportClick}
            disabled={exportLoading}
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
          </button>
          <PermissionGate permission="target_achievement" action="create">
            <button
              onClick={handleCreateSchedule}
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
            >
              <Calendar className="h-4 w-4" />
              <span>Create Schedule</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4">
        <div className="flex flex-row flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={scheduleSearchQuery}
              onChange={(e) => {
                handleSearchInputChange(e, (value) => {
                  setScheduleSearchQuery(value);
                  setCurrentPage(1); // Reset to first page on search
                }, FIELD_LIMITS.search);
              }}
              placeholder="Search schedules by name, frequency, or time..."
              maxLength={FIELD_LIMITS.search}
              className="input-field pl-10 pr-4 py-2 w-full"
              autoComplete="off"
            />
          </div>

          <div className="flex flex-row gap-3 items-center">
            <DateRangeFilter
              startDate={customRange.startDate}
              endDate={customRange.endDate}
              onChange={(newRange) => setCustomRange((prev) => ({ ...prev, ...newRange }))}
              allowFutureDates={false}
            />

            {(customRange.startDate || customRange.endDate || (scheduleSearchQuery && scheduleSearchQuery.trim())) && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setScheduleSearchQuery('');
                  setCustomRange({ startDate: '', endDate: '' });
                  setCurrentPage(1);
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
                className="flex-shrink-0 whitespace-nowrap"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Schedules List - Table Format */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                      checked={getSortedSchedules(displaySchedules).length > 0 && getSortedSchedules(displaySchedules).every(item => selectedRows.has(item.id || item._id || item.scheduleId))}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Frequency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Next Run
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className="animate-pulse">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-28"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-7 bg-gray-200 rounded w-16"></div>
                        <div className="h-7 bg-gray-200 rounded w-14"></div>
                        <div className="h-7 bg-gray-200 rounded w-16"></div>
                        <div className="h-7 bg-gray-200 rounded w-16"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : schedules.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">

          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                      checked={getSortedSchedules(displaySchedules).length > 0 && getSortedSchedules(displaySchedules).every(item => selectedRows.has(item.id || item._id || item.scheduleId))}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-brand/5 hover:text-brand transition-all select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Schedule Name</span>
                      {sortColumn === 'name' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-brand/5 hover:text-brand transition-all select-none"
                    onClick={() => handleSort('frequency')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Frequency</span>
                      {sortColumn === 'frequency' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-brand/5 hover:text-brand transition-all select-none"
                    onClick={() => handleSort('nextRun')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Next Run</span>
                      {sortColumn === 'nextRun' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-brand/5 hover:text-brand transition-all select-none"
                    onClick={() => handleSort('enabled')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'enabled' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getSortedSchedules(displaySchedules).map((schedule) => {
                  const scheduleId = schedule.id || schedule._id || schedule.scheduleId;
                  const frequencyText = schedule.frequency === 'weekly' && schedule.day
                    ? `${schedule.frequency} (${schedule.day})`
                    : schedule.frequency;

                  return (
                    <tr key={scheduleId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                          checked={selectedRows.has(scheduleId)}
                          onChange={() => handleSelectRow(scheduleId)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={schedule.name}>
                          {schedule.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">
                          {frequencyText}
                        </div>
                        <div className="text-xs text-gray-500">
                          {schedule.time}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {schedule.nextRun ? formatDateTime(schedule.nextRun) : 'N/A'}
                        </div>
                        {schedule.lastRun && (
                          <div className="text-xs text-gray-500">
                            Last: {formatDateTime(schedule.lastRun)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${schedule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}
                          title={
                            !schedule.enabled && schedule.disabledReason
                              ? getDisabledReasonLabel(schedule.disabledReason)
                              : schedule.enabled
                                ? 'Schedule is active'
                                : 'Schedule is inactive'
                          }
                        >
                          {schedule.enabled
                            ? 'Active'
                            : schedule.disabledReason
                              ? getDisabledReasonLabel(schedule.disabledReason)
                              : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium overflow-visible">
                        <div className="flex items-center justify-end space-x-3">
                          <PermissionGate permission="target_achievement" action="read">
                            <button
                              onClick={(e) => handleViewSchedule(schedule, e)}
                              disabled={isAnyMutationPending}
                              type="button"
                              className="text-brand hover:text-brand/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="View Schedule"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="target_achievement" action="update">
                            <button
                              onClick={(e) => handleEditSchedule(schedule, e)}
                              disabled={isAnyMutationPending}
                              type="button"
                              className="text-amber-600 hover:text-amber-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Edit Schedule"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="target_achievement" action="update">
                            <button
                              onClick={(e) => handleRunSchedule(scheduleId, e)}
                              disabled={runScheduleMutation.isPending || !schedule.enabled}
                              type="button"
                              className="text-brand hover:text-brand/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title={!schedule.enabled
                                ? schedule.disabledReason
                                  ? getDisabledReasonLabel(schedule.disabledReason)
                                  : 'Enable schedule first to run'
                                : 'Run Schedule Now'}
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="target_achievement" action="update">
                            <button
                              onClick={(e) => handleToggleSchedule(scheduleId, e)}
                              disabled={toggleScheduleMutation.isPending}
                              type="button"
                              className={`${schedule.enabled
                                ? 'text-yellow-600 hover:text-yellow-900'
                                : 'text-green-600 hover:text-green-900'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              title={schedule.enabled ? 'Disable Schedule' : 'Enable Schedule'}
                            >
                              {schedule.enabled ? (
                                <XCircle className="h-4 w-4" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="target_achievement" action="delete">
                            <button
                              onClick={(e) => handleDeleteSchedule(scheduleId, e)}
                              disabled={loading || deletingScheduleId === scheduleId}
                              type="button"
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={deletingScheduleId === scheduleId ? 'Deleting...' : 'Delete Schedule'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            onExportCurrentPage={handleExportCurrentPage}
            onExportAll={handleExportAll}
            onExportRange={handleExportRange}
            loading={exportLoading}
            title="Export Schedules"
            entityName="Schedules"
          />

          {/* Pagination */}
          {(() => {
            // Show pagination if there's more than one page (same as LeadListFilters)
            const shouldShowPagination = !loading && totalPages > 1;



            return shouldShowPagination ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                  {/* Results Count and Limit Selector */}
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-700">
                      Showing {startIndex} to {endIndex} of {totalSchedules} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Items per page:</label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="input-field px-2 py-1 text-sm rounded-lg"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSchedulePageChange((currentPageNum || currentPage) - 1)}
                      disabled={pagination?.hasPrevPage === false || (currentPageNum || currentPage) <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const pages = [];
                        const activePage = currentPageNum || currentPage;

                        if (totalPages <= 10) {
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push(1);

                          if (activePage <= 4) {
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          } else if (activePage >= totalPages - 3) {
                            pages.push('ellipsis');
                            for (let i = totalPages - 4; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push('ellipsis');
                            for (let i = activePage - 1; i <= activePage + 1; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === 'ellipsis') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={page}
                              onClick={() => handleSchedulePageChange(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === activePage
                                ? 'bg-brand text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-brand/5 hover:text-brand transition-all'
                                }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => handleSchedulePageChange((currentPageNum || currentPage) + 1)}
                      disabled={pagination?.hasNextPage === false || (currentPageNum || currentPage) >= totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      ) : scheduleSearchQuery.trim() ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <SearchIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
            <p className="text-gray-600 mb-4">
              No schedules found matching <span className="font-semibold">"{scheduleSearchQuery}"</span>
            </p>
            <button
              onClick={() => {
                setScheduleSearchQuery('');
                setCurrentPage(1);
              }}
              className="btn-create px-6 py-2"
            >
              Clear Search
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Scheduled Reports</h3>
            <p className="text-gray-600 mb-4">Create your first automated report schedule</p>
            <PermissionGate permission="target_achievement" action="create">
              <button
                onClick={handleCreateSchedule}
                className="btn-create px-6 py-2"
              >
                Create Schedule
              </button>
            </PermissionGate>
          </div>
        </div>
      )
      }

      {/* Schedule Creation Modal */}
      {
        showScheduleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingScheduleId ? 'Edit Schedule' : 'Create Schedule'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowScheduleModal(false);
                      setErrors({});
                      setTargetSearchQuery(''); // Reset search when closing modal
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form className="p-6 space-y-6" onSubmit={handleSubmitSchedule} noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={scheduleForm.name}
                      onChange={(e) => {
                        handleTextInputChange(e, (value) => {
                          // Auto-capitalize first letter of schedule name
                          const formatted = capitalizeFirst(value);
                          setScheduleForm(prev => ({ ...prev, name: formatted }));
                        }, FIELD_LIMITS.customerName);
                        clearValidationError('name');
                      }}
                      onBlur={() => {
                        const error = validateField('name', scheduleForm.name);
                        if (error) {
                          setValidationErrors(prev => ({ ...prev, name: error }));
                        }
                      }}
                      maxLength={validationRules.name.maxLength}
                      className={`input-field w-full ${(validationErrors.name || errors.name) ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Enter schedule name"
                    />
                    <div className="mt-1 flex justify-between items-center">
                      <div>
                        {(validationErrors.name || errors.name) && (
                          <p className="text-sm text-red-600">{validationErrors.name || errors.name}</p>
                        )}
                      </div>
                      <p className={`text-xs ${scheduleForm.name.length > validationRules.name.maxLength * 0.9 ? 'text-red-600' : 'text-gray-500'}`}>
                        {scheduleForm.name.length}/{validationRules.name.maxLength} characters
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Frequency <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={scheduleForm.frequency}
                      onChange={(e) => {
                        setScheduleForm(prev => ({ ...prev, frequency: e.target.value }));
                        clearValidationError('frequency');
                        // Clear day error if frequency is not weekly
                        if (e.target.value !== 'weekly') {
                          clearValidationError('day');
                        }
                      }}
                      onBlur={() => {
                        const error = validateField('frequency', scheduleForm.frequency);
                        if (error) {
                          setValidationErrors(prev => ({ ...prev, frequency: error }));
                        }
                      }}
                      className={`input-field w-full ${(validationErrors.frequency || errors.frequency) ? 'border-red-500 focus:ring-red-500' : ''}`}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    {(validationErrors.frequency || errors.frequency) && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.frequency || errors.frequency}</p>
                    )}
                  </div>

                  {scheduleForm.frequency === 'weekly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Day of Week <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={scheduleForm.day}
                        onChange={(e) => {
                          setScheduleForm(prev => ({ ...prev, day: e.target.value }));
                          clearValidationError('day');
                        }}
                        onBlur={() => {
                          if (scheduleForm.frequency === 'weekly') {
                            const error = validateField('day', scheduleForm.day);
                            if (error) {
                              setValidationErrors(prev => ({ ...prev, day: error }));
                            }
                          }
                        }}
                        className={`input-field w-full ${(validationErrors.day || errors.day) ? 'border-red-500 focus:ring-red-500' : ''}`}
                      >
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                      {(validationErrors.day || errors.day) && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.day || errors.day}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={scheduleForm.time}
                      onChange={(e) => {
                        setScheduleForm(prev => ({ ...prev, time: e.target.value }));
                        clearValidationError('time');
                      }}
                      onBlur={() => {
                        const error = validateField('time', scheduleForm.time);
                        if (error) {
                          setValidationErrors(prev => ({ ...prev, time: error }));
                        }
                      }}
                      className={`input-field w-full ${(validationErrors.time || errors.time) ? 'border-red-500 focus:ring-red-500' : ''}`}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    />
                    {(validationErrors.time || errors.time) && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.time || errors.time}</p>
                    )}
                  </div>
                </div>

                {/* Target selection - multi-select, target-driven */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Targets <span className="text-red-500">*</span>
                  </label>

                  <div className={`border rounded-md bg-gray-50 ${(validationErrors.targets || errors.targets) ? 'border-red-300' : 'border-gray-300'}`}>
                    {/* Search Input - Inside the selector */}
                    {/* Search Input and Selected Chips - Inside the selector */}
                    <div className="sticky top-0 z-10 bg-white border-b border-gray-300 px-3 py-2">
                      {/* Selected Targets Chips */}
                      {scheduleForm.targets.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {scheduleForm.targets.map(targetId => {
                            const target = availableTargets.find(t => (t.id || t._id) === targetId);
                            if (!target) return null;

                            const typeLabel =
                              target.type === 'revenue'
                                ? 'Revenue'
                                : target.type === 'newOpps'
                                  ? 'New Opps'
                                  : target.type === 'deals'
                                    ? 'Deals'
                                    : target.type;

                            let displayName = target.name || `${typeLabel} - ${target.period}`;
                            // Clean up name
                            displayName = displayName.replace(/\s*\(Individual\)\s*$/i, '');
                            if (target.scope === 'team' && target.teamName && target.teamName.trim() && target.teamName !== 'Unknown') {
                              displayName = target.teamName;
                            }

                            return (
                              <div key={targetId} className="inline-flex items-center bg-brand/10 text-brand px-2 py-1 rounded-md text-xs border border-brand/20">
                                <span className="truncate max-w-[150px]" title={displayName}>{displayName}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setScheduleForm(prev => {
                                      const newTargets = prev.targets.filter(id => id !== targetId);

                                      // Validate immediately on removal
                                      if (newTargets.length === 0) {
                                        setValidationErrors(prevErrors => ({ ...prevErrors, targets: 'Please select at least one target' }));
                                      } else {
                                        clearValidationError('targets');
                                      }

                                      return {
                                        ...prev,
                                        targets: newTargets
                                      };
                                    });
                                  }}
                                  className="ml-1 text-brand/60 hover:text-brand focus:outline-none"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <SearchIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={targetSearchQuery}
                          onChange={(e) => {
                            handleSearchInputChange(e, setTargetSearchQuery, FIELD_LIMITS.search);
                          }}
                          placeholder="Search targets by name, type, or team..."
                          maxLength={FIELD_LIMITS.search}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-white transition-all shadow-sm"
                          onClick={(e) => e.stopPropagation()}
                          autoComplete="off"
                        />

                        {searchLoading && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand"></div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Target List */}
                    <div className="max-h-48 overflow-y-auto px-3 py-2 space-y-1">
                      {availableTargets.length === 0 ? (
                        <p className="text-xs text-gray-500 py-2 text-center">
                          {searchLoading
                            ? 'Searching...'
                            : targetSearchQuery.trim()
                              ? `No targets found matching "${targetSearchQuery}"`
                              : 'No targets loaded yet. Create targets first from the Created Targets page.'}
                        </p>
                      ) : (
                        availableTargets.map((t) => {
                          const id = t.id || t._id;
                          const checked = Array.isArray(scheduleForm.targets) && scheduleForm.targets.includes(id);
                          const typeLabel =
                            t.type === 'revenue'
                              ? 'Revenue'
                              : t.type === 'newOpps'
                                ? 'New Opportunities'
                                : t.type === 'deals'
                                  ? 'Deal Count'
                                  : t.type;

                          // Fix: For team targets, use teamName if available, otherwise use name
                          // For individual targets, use name
                          let displayName = t.name || `${typeLabel} - ${t.period}`;

                          // Remove any "(Individual)" suffix that might be incorrectly added
                          displayName = displayName.replace(/\s*\(Individual\)\s*$/i, '');

                          // For team targets, prefer teamName if available
                          if (t.scope === 'team' && t.teamName && t.teamName.trim() && t.teamName !== 'Unknown') {
                            displayName = t.teamName;
                          }

                          const scopeLabel = t.scope === 'team' ? 'Team' : 'Individual';

                          return (
                            <label
                              key={id}
                              className="flex items-center gap-3 text-sm text-gray-700 py-2 px-2 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const isChecked = e.target.checked;
                                  setScheduleForm((prev) => {
                                    const current = Array.isArray(prev.targets) ? prev.targets : [];
                                    if (isChecked) {
                                      if (current.includes(id)) return prev;
                                      return { ...prev, targets: [...current, id] };
                                    }
                                    return {
                                      ...prev,
                                      targets: current.filter((targetId) => targetId !== id)
                                    };
                                  });
                                  clearValidationError('targets');
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate" title={displayName}>
                                  {displayName}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                                  <span>{typeLabel}</span>
                                  <span>•</span>
                                  <span>{scopeLabel}</span>
                                  {t.scope === 'team' && t.teamName && t.teamName !== 'Unknown' && (
                                    <>
                                      <span>•</span>
                                      <span className="text-brand font-medium">{t.teamName}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                  {(validationErrors.targets || errors.targets) && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.targets || errors.targets}</p>
                  )}
                </div>

                {/* (Old metrics / scope UI removed intentionally – schedules are purely target-driven now) */}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={scheduleForm.enabled}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                  />
                  <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
                    Enable this schedule
                  </label>
                </div>

                <div className="flex items-center space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowScheduleModal(false);
                      setErrors({});
                    }}
                    className="btn-cancel px-6 py-2 ml-auto"
                  >
                    Cancel
                  </button>
                  <PermissionGate permission="target_achievement" action={editingScheduleId ? "update" : "create"}>
                    <button
                      type="submit"
                      disabled={createUpdateScheduleMutation.isPending}
                      className="btn-create px-8 py-2 min-w-[140px]"
                    >
                      {createUpdateScheduleMutation.isPending
                        ? (editingScheduleId ? 'Saving...' : 'Creating...')
                        : (editingScheduleId ? 'Update Schedule' : 'Create Schedule')}
                    </button>
                  </PermissionGate>
                </div>
              </form>
            </div>
          </div >
        )
      }
    </div >
  );
};

export default Schedules;
